import { createContext, useContext, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { sleep } from 'common/commonUtil';
import { SocketResponse } from 'common/SocketResponseDocument';

export const SocketContext = createContext<Socket | null>(null);

interface SocketProps {
  listeners?: {
    path: string;
    handler: (data: any) => void;
  }[];
}

export const cleanUpSocket = (socket: Socket) => {
  socket.removeAllListeners();
  socket.disconnect();
};

export const useSocket = ({ listeners }: SocketProps) => {
  const socket = useContext<Socket | null>(SocketContext)!;

  const send = <SocketRequestPayload, SocketResponsePayload extends SocketResponse = SocketResponse>(
    path: string,
    request?: SocketRequestPayload,
    options?: { timeout?: number | null }
  ): Promise<SocketResponsePayload> => {
    return new Promise(async (resolve) => {
      const timeout = options?.timeout || 10000;
      let timedOut = false;
      let timeoutRef: NodeJS.Timeout | null = null;
      let isError = false;

      const errorHandler = (error: Error) => {
        console.log('useSocket error', error);
        isError = true;
        if (timeoutRef) clearTimeout(timeoutRef);
        resolve({ status: error.message } as SocketResponsePayload);
      };

      if (!socket.connected) {
        // wait for connection for 5s, this is to prevent errors when the socket is not ready when calling it
        for (let i = 0; i < 5; i++) {
          await sleep(1000);
          if (socket.connected) break;
        }
        if (!socket.connected) {
          resolve({ status: 'socket not connected' } as SocketResponsePayload);
        }
      }

      // Handle error from any socket middleware
      socket.on('connect_error', errorHandler);

      socket.emit(path, request ?? {}, (response: SocketResponsePayload) => {
        if (isError) return;
        socket.removeListener('connect_error', errorHandler);
        if (!timedOut) resolve(response);
        if (timeoutRef) clearTimeout(timeoutRef);
      });

      if (timeout) {
        timeoutRef = setTimeout(() => {
          if (isError) return;
          timedOut = true;
          console.error('Socket request timed out', { path, request, options });
          socket.removeListener('connect_error', errorHandler);
          resolve({ status: 'timeout' } as SocketResponsePayload);
        }, timeout);
      }
    });
  };

  useEffect(() => {
    if (listeners) {
      for (const listener of listeners) {
        socket.on(listener.path, (payload) => listener.handler(payload));
      }
    }

    return () => {
      if (!listeners) return;

      for (const listener of listeners) {
        socket.off(listener.path);
      }
    };
  }, []);

  return { send, onAny: socket.onAny };
};
